import { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, Eye, Loader2, LockKeyhole, Settings2 } from 'lucide-react';

import { QuickPunchConfigurationEditor } from '@/features/punch-lists/components/QuickPunchConfigurationEditor';
import {
  useCostCodes,
  useProjectUsers,
  usePunchListItemTypes,
  usePunchListMutations,
  useQuickPunchConfiguration,
  useTrades,
  useTradePartners,
  useUpdateQuickPunchPreference,
} from '@/features/punch-lists/hooks';
import type {
  CreatePunchListItemData,
  ImpactChoice,
  PunchListPriority,
  QuickPunchConfigurationResponse,
  QuickPunchDefaultValue,
  QuickPunchFieldDefinition,
  QuickPunchFieldKey,
} from '@/features/punch-lists/types';
import { getErrorMessage } from '@/shared/errors';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';

import { useCreateHotspot } from '../hooks/useHotspotData';
import type { ViewPosition } from '../types/types';
import type { PendingPhotoUpload } from './pendingPhotoUpload';

interface QuickPunchItemHotspotFormProps {
  photoId: number;
  projectId: number | undefined;
  viewPosition: ViewPosition;
  hotspotId?: number;
  pendingUploads: PendingPhotoUpload[];
  onRequestPhoto: () => void;
  onSuccess: () => void | Promise<void>;
  onCancel: () => void;
}

type QuickPunchValues = Partial<Record<QuickPunchFieldKey, QuickPunchDefaultValue>>;

const PRIORITIES = [
  ['LOW', 'Low'],
  ['MEDIUM', 'Medium'],
  ['HIGH', 'High'],
] as const;

const IMPACTS = [
  ['YES', 'Yes'],
  ['YES_UNKNOWN', 'Yes (Unknown)'],
  ['NO', 'No'],
  ['TBD', 'TBD'],
  ['NA', 'N/A'],
] as const;

const displayUser = (user: { first_name: string; last_name: string; email: string }) =>
  `${user.first_name} ${user.last_name}`.trim() || user.email;

interface ConfiguredQuickPunchFormProps extends QuickPunchItemHotspotFormProps {
  configuration: QuickPunchConfigurationResponse;
  onEditConfiguration: () => void;
  showConfigurationPreview: boolean;
}

const ConfiguredQuickPunchForm = ({
  photoId,
  projectId,
  viewPosition,
  hotspotId: existingHotspotId,
  pendingUploads,
  onRequestPhoto,
  onSuccess,
  onCancel,
  configuration,
  onEditConfiguration,
  showConfigurationPreview,
}: ConfiguredQuickPunchFormProps) => {
  const [values, setValues] = useState<QuickPunchValues>(() => ({
    ...configuration.effective.defaults,
    description: '',
  }));
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { data: itemTypes = [] } = usePunchListItemTypes();
  const { data: trades = [] } = useTrades();
  const { data: tradePartners = [] } = useTradePartners(projectId);
  const { data: projectUsers = [] } = useProjectUsers(projectId);
  const { data: costCodes = [] } = useCostCodes();
  const { createQuickItem, uploadAttachment, linkAttachmentPhoto } =
    usePunchListMutations(projectId);
  const createHotspotMutation = useCreateHotspot(projectId == null ? undefined : String(projectId));

  const visibleFieldDefinitions = useMemo(() => {
    const visible = new Set(configuration.effective.visible_fields);
    return configuration.available_fields.filter((field) => visible.has(field.key));
  }, [configuration]);
  const hiddenDefaultFieldDefinitions = useMemo(() => {
    const visible = new Set(configuration.effective.visible_fields);
    const configuredDefaults = {
      ...(configuration.user_preference?.defaults ?? {}),
      ...configuration.project_configuration.defaults,
    };
    return configuration.available_fields.filter((field) => {
      const value = configuredDefaults[field.key];
      return !visible.has(field.key) && value !== null && value !== undefined && value !== '';
    });
  }, [configuration]);
  const required = useMemo(() => new Set(configuration.effective.required_fields), [configuration]);
  const enforcedDefaults = useMemo(
    () => new Set(Object.keys(configuration.project_configuration.defaults)),
    [configuration]
  );
  const photo = pendingUploads[0];

  const setValue = (key: QuickPunchFieldKey, value: QuickPunchDefaultValue) =>
    setValues((current) => ({ ...current, [key]: value }));

  const getOrCreateHotspot = async () => {
    if (existingHotspotId) return existingHotspotId;
    const hotspot = await createHotspotMutation.mutateAsync({
      photoId,
      position: {
        pitch: viewPosition.pitch,
        yaw: viewPosition.yaw,
        hfov: viewPosition.hfov,
      },
    });
    return hotspot.id;
  };

  const selectField = (
    field: QuickPunchFieldDefinition,
    options: Array<{ value: string; label: string }>,
    disabled: boolean,
    fieldId: string,
    ariaLabel?: string
  ) => (
    <Select
      value={
        values[field.key] == null || values[field.key] === '' ? 'none' : String(values[field.key])
      }
      onValueChange={(value) => setValue(field.key, value === 'none' ? null : value)}
      disabled={disabled}
    >
      <SelectTrigger id={fieldId} aria-label={ariaLabel}>
        <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
      </SelectTrigger>
      <SelectContent>
        {!required.has(field.key) && <SelectItem value="none">None</SelectItem>}
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const renderField = (field: QuickPunchFieldDefinition, forceDisabled = false) => {
    const disabled = forceDisabled || enforcedDefaults.has(field.key);
    const fieldId = forceDisabled
      ? `quick-punch-applied-default-${field.key}`
      : `quick-punch-${field.key}`;
    const ariaLabel = forceDisabled ? `${field.label} applied default` : undefined;
    switch (field.kind) {
      case 'textarea':
        return (
          <Textarea
            id={fieldId}
            aria-label={ariaLabel}
            value={String(values[field.key] ?? '')}
            onChange={(event) => setValue(field.key, event.target.value)}
            placeholder="Describe what needs to be corrected"
            className="min-h-28 resize-y"
            autoFocus={!forceDisabled}
            disabled={disabled}
          />
        );
      case 'text':
        return (
          <Input
            id={fieldId}
            aria-label={ariaLabel}
            value={String(values[field.key] ?? '')}
            onChange={(event) => setValue(field.key, event.target.value)}
            placeholder="Quick Punch title"
            disabled={disabled}
          />
        );
      case 'priority':
        return selectField(
          field,
          PRIORITIES.map(([value, label]) => ({ value, label })),
          disabled,
          fieldId,
          ariaLabel
        );
      case 'impact':
        return selectField(
          field,
          IMPACTS.map(([value, label]) => ({ value, label })),
          disabled,
          fieldId,
          ariaLabel
        );
      case 'item_type':
        return selectField(
          field,
          itemTypes.map((item) => ({ value: item.id, label: item.name })),
          disabled,
          fieldId,
          ariaLabel
        );
      case 'trade':
        return selectField(
          field,
          trades.map((trade) => ({ value: trade.id, label: trade.name })),
          disabled,
          fieldId,
          ariaLabel
        );
      case 'trade_partner':
        return selectField(
          field,
          tradePartners.map((partner) => ({
            value: String(partner.id),
            label: partner.company_name,
          })),
          disabled,
          fieldId,
          ariaLabel
        );
      case 'project_user':
        return selectField(
          field,
          projectUsers.map((user) => ({ value: String(user.id), label: displayUser(user) })),
          disabled,
          fieldId,
          ariaLabel
        );
      case 'cost_code':
        return selectField(
          field,
          costCodes.map((code) => ({ value: code.id, label: `${code.code} – ${code.name}` })),
          disabled,
          fieldId,
          ariaLabel
        );
      case 'datetime':
        return (
          <Input
            id={fieldId}
            aria-label={ariaLabel}
            type="datetime-local"
            value={String(values[field.key] ?? '')}
            onChange={(event) => setValue(field.key, event.target.value || null)}
            disabled={disabled}
          />
        );
      case 'boolean':
        return (
          <div className="flex h-10 items-center gap-2 rounded-[10px] border border-border bg-card px-3">
            <Checkbox
              id={fieldId}
              aria-label={ariaLabel}
              checked={values[field.key] === true || values[field.key] === 'true'}
              onCheckedChange={(checked) => setValue(field.key, checked === true)}
              disabled={disabled}
            />
            <span className="text-sm text-muted-foreground">Only visible to authorized users</span>
          </div>
        );
      default:
        return null;
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!photo) {
      setError('A photo is required. Go back and take a photo.');
      return;
    }
    if (!projectId) {
      setError('No project context is available.');
      return;
    }
    const missing = configuration.effective.required_fields.find((field) => {
      const value = values[field];
      return value == null || value === '';
    });
    if (missing) {
      const definition = configuration.available_fields.find((field) => field.key === missing);
      setError(`${definition?.label ?? missing} is required.`);
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      const hotspotId = await getOrCreateHotspot();
      const payload = {
        ...values,
        project: projectId,
        hotspot: hotspotId,
        description: String(values.description ?? '').trim(),
        priority: values.priority as PunchListPriority | undefined,
        cost_impact: values.cost_impact as ImpactChoice | undefined,
        schedule_impact: values.schedule_impact as ImpactChoice | undefined,
        assignee: values.assignee == null ? null : Number(values.assignee),
        punch_manager: values.punch_manager == null ? null : Number(values.punch_manager),
        final_approver: values.final_approver == null ? null : Number(values.final_approver),
        responsible_trade_partner:
          values.responsible_trade_partner == null
            ? null
            : Number(values.responsible_trade_partner),
        is_private: values.is_private === true || values.is_private === 'true',
      } as Partial<CreatePunchListItemData> &
        Pick<CreatePunchListItemData, 'project' | 'description'>;
      const newItem = await createQuickItem.mutateAsync(payload);

      const uploadedPhoto = await photo.uploadPromise.catch(() => null);
      if (uploadedPhoto) {
        await linkAttachmentPhoto.mutateAsync({
          itemId: newItem.id,
          photoId: uploadedPhoto.photoId,
          kind: 'before',
        });
      } else {
        await uploadAttachment.mutateAsync({ itemId: newItem.id, file: photo.file, kind: 'before' });
      }
      await onSuccess();
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pt-1">
      {showConfigurationPreview && (
        <div className="flex items-start gap-3 rounded-[13px] border border-primary/25 bg-primary/10 p-4">
          <Eye className="mt-0.5 size-5 shrink-0 text-primary" strokeWidth={1.9} />
          <div>
            <p className="text-sm font-semibold text-foreground">Your Quick Punch form is ready</p>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              This is the form created from your selections. Review it below, make a Quick Punch
              now, or update the fields and defaults again.
            </p>
          </div>
        </div>
      )}
      <div className="grid gap-5 lg:grid-cols-[minmax(15rem,0.72fr)_minmax(0,1.7fr)] lg:gap-6">
        <div className="lg:sticky lg:top-0 lg:self-start">
          <div className="overflow-hidden rounded-[13px] border border-border bg-muted">
            {photo ? (
              <img
                src={photo.previewUrl}
                alt="Quick punch item"
                className="max-h-60 w-full object-cover lg:aspect-[4/5] lg:max-h-[34rem]"
              />
            ) : (
              <div className="grid min-h-40 place-items-center px-5 text-center text-muted-foreground lg:min-h-80">
                <div>
                  <Camera className="mx-auto size-8" aria-hidden="true" />
                  <p className="mt-2 text-xs">A photo is required for Quick Punch.</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={onRequestPhoto}
                  >
                    Take photo
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div className="mt-3 rounded-[13px] border border-border bg-muted px-4 py-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
              Project policy
            </p>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Project defaults are locked. Required fields without defaults remain editable.
            </p>
          </div>
        </div>

        <div className="min-w-0 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                Quick Punch details
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Complete the fields below, then create the item.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={onEditConfiguration}
            >
              <Settings2 className="size-3.5" /> Update fields & defaults
            </Button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {visibleFieldDefinitions.map((field) => (
              <div
                key={field.key}
                className={`space-y-1.5 ${field.kind === 'textarea' ? 'sm:col-span-2' : ''}`}
              >
                <Label htmlFor={`quick-punch-${field.key}`}>
                  {field.label}
                  {required.has(field.key) && <span className="text-destructive"> *</span>}
                  {enforcedDefaults.has(field.key) && (
                    <span className="ml-1 text-xs text-primary">Project default</span>
                  )}
                </Label>
                {renderField(field)}
              </div>
            ))}
          </div>

          {hiddenDefaultFieldDefinitions.length > 0 && (
            <section
              className="border-t border-border pt-5"
              aria-labelledby="applied-defaults-title"
            >
              <div className="mb-3 flex items-start gap-2.5">
                <LockKeyhole className="mt-0.5 size-4 shrink-0 text-primary" strokeWidth={1.9} />
                <div>
                  <p id="applied-defaults-title" className="text-sm font-semibold text-foreground">
                    Applied defaults
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    These hidden values are applied automatically and cannot be edited on this form.
                  </p>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {hiddenDefaultFieldDefinitions.map((field) => (
                  <div
                    key={field.key}
                    className="space-y-2 rounded-[13px] border border-border bg-muted p-3.5"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <Label htmlFor={`quick-punch-applied-default-${field.key}`}>
                        {field.label}
                      </Label>
                      <span className="rounded-full bg-primary/12 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.06em] text-primary">
                        Applied automatically
                      </span>
                    </div>
                    {renderField(field, true)}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Back
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting && <Loader2 className="mr-2 size-4 animate-spin" aria-hidden="true" />}
          Create Quick Punch
        </Button>
      </div>
    </form>
  );
};

export const QuickPunchItemHotspotForm = (props: QuickPunchItemHotspotFormProps) => {
  const { projectId, onCancel, onRequestPhoto, pendingUploads } = props;
  const configurationQuery = useQuickPunchConfiguration(projectId);
  const updatePreference = useUpdateQuickPunchPreference(projectId);
  const [editingConfiguration, setEditingConfiguration] = useState(false);
  const [showConfigurationPreview, setShowConfigurationPreview] = useState(false);
  const [configurationError, setConfigurationError] = useState<string | null>(null);
  const hasRequestedInitialPhoto = useRef(false);
  const configuration = configurationQuery.data;

  useEffect(() => {
    const isReadyForPhoto =
      configuration?.user_preference != null &&
      !editingConfiguration &&
      pendingUploads.length === 0;
    if (!isReadyForPhoto || hasRequestedInitialPhoto.current) return;
    hasRequestedInitialPhoto.current = true;
    onRequestPhoto();
  }, [configuration, editingConfiguration, onRequestPhoto, pendingUploads.length]);

  if (configurationQuery.isLoading) {
    return (
      <div className="grid min-h-52 place-items-center text-sm text-muted-foreground">
        <Loader2 className="mb-2 size-5 animate-spin text-primary" />
        Loading your Quick Punch setup…
      </div>
    );
  }

  if (configurationQuery.isError || !configuration || !projectId) {
    return (
      <div className="space-y-4 py-4">
        <p className="text-sm text-destructive">
          {configurationQuery.error
            ? getErrorMessage(configurationQuery.error)
            : 'Quick Punch configuration is unavailable.'}
        </p>
        <Button type="button" variant="outline" onClick={onCancel}>
          Back
        </Button>
      </div>
    );
  }

  const needsInitialSetup = configuration.user_preference == null;
  if (needsInitialSetup || editingConfiguration) {
    const lockedFields = new Set(configuration.effective.locked_fields);
    const lockedDefaults = new Set(
      Object.keys(configuration.project_configuration.defaults) as QuickPunchFieldKey[]
    );
    const userDefaults = configuration.user_preference?.defaults ?? {};
    return (
      <div className="space-y-3">
        <QuickPunchConfigurationEditor
          key={`${needsInitialSetup ? 'initial' : 'edit'}-${configuration.project_configuration.updated_at ?? 'none'}`}
          projectId={projectId}
          fields={configuration.available_fields}
          mode="user"
          selectedFields={
            configuration.user_preference?.visible_fields ?? configuration.effective.visible_fields
          }
          requiredFields={configuration.user_preference?.required_fields ?? []}
          defaults={{ ...userDefaults, ...configuration.project_configuration.defaults }}
          lockedFields={[...lockedFields]}
          lockedDefaultFields={[...lockedDefaults]}
          submitLabel={needsInitialSetup ? 'Save & preview form' : 'Update & preview form'}
          isSaving={updatePreference.isPending}
          onCancel={needsInitialSetup ? onCancel : () => setEditingConfiguration(false)}
          onSave={async ({ selectedFields, requiredFields, defaults }) => {
            setConfigurationError(null);
            const editableDefaults = Object.fromEntries(
              Object.entries(defaults).filter(
                ([key]) => !lockedDefaults.has(key as QuickPunchFieldKey)
              )
            );
            const editableRequiredFields = requiredFields.filter(
              (field) => field !== 'description' && !lockedFields.has(field)
            );
            try {
              await updatePreference.mutateAsync({
                visible_fields: selectedFields,
                required_fields: editableRequiredFields,
                defaults: editableDefaults,
              });
              setShowConfigurationPreview(true);
              setEditingConfiguration(false);
            } catch (caught) {
              setConfigurationError(getErrorMessage(caught));
            }
          }}
        />
        {configurationError && <p className="text-sm text-destructive">{configurationError}</p>}
      </div>
    );
  }

  return (
    <ConfiguredQuickPunchForm
      key={[
        ...(configuration.user_preference?.visible_fields ?? []),
        '|required|',
        ...(configuration.user_preference?.required_fields ?? []),
      ].join('|')}
      {...props}
      configuration={configuration}
      onEditConfiguration={() => setEditingConfiguration(true)}
      showConfigurationPreview={showConfigurationPreview}
      onCancel={
        showConfigurationPreview
          ? () => {
              setShowConfigurationPreview(false);
              setEditingConfiguration(true);
            }
          : onCancel
      }
    />
  );
};
